import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { resolveImage } from "@/lib/api";
import DOMPurify from "dompurify";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const renderContent = (content) => {
  if (!content) return { html: null };
  if (content.trim().startsWith("<")) {
    const absolute = content.replaceAll('src="/api/', `src="${API_BASE}/api/`);
    return { html: DOMPurify.sanitize(absolute) };
  }
  return { html: null };
};
import { Footer } from "@/components/site/Footer";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/posts/${slug}`)
      .then((r) => { setPost(r.data); setStatus("ok"); })
      .catch(() => setStatus("missing"));
  }, [slug]);

  return (
    <div className="bg-bone min-h-screen" data-testid="blog-post-page">
      <div className="noise-overlay" />
      <header className="border-b border-forest/15 bg-bone/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/blog" data-testid="post-back-link" className="group flex items-center gap-2 text-sm font-bold text-forest hover:text-terra transition-colors">
            <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
            All stories
          </Link>
          <Link to="/" className="font-serif italic font-bold text-forest text-lg" data-testid="post-home-link">Learning Lights</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        {status === "loading" && <p className="font-serif italic text-moss text-xl" data-testid="post-loading">Turning the page…</p>}
        {status === "missing" && (
          <div data-testid="post-not-found">
            <h1 className="font-serif font-bold text-forest text-4xl tracking-tighter">Story not found.</h1>
            <p className="mt-4 text-moss">It may have been unpublished. <Link to="/blog" className="text-terra font-bold">Browse all stories</Link>.</p>
          </div>
        )}
        {status === "ok" && post && (
          <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="post-content">
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra" data-testid="post-date">
              {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              {" "}· {post.author}
            </span>
            <h1 className="font-serif font-bold tracking-tighter text-forest text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]" data-testid="post-title">
              {post.title}
            </h1>
            {post.cover_image && (
              <div className="relative mt-10">
                <div className="absolute -inset-0 translate-x-4 translate-y-4 bg-terra/85 -z-0" />
                <img src={resolveImage(post.cover_image)} alt={post.title} className="relative w-full max-h-[440px] object-cover img-warm border border-forest/20" data-testid="post-cover" />
              </div>
            )}
            {renderContent(post.content).html ? (
              <div className="post-body mt-10" data-testid="post-body"
                dangerouslySetInnerHTML={{ __html: renderContent(post.content).html }} />
            ) : (
              <div className="mt-10 space-y-6" data-testid="post-body">
                {post.content.split(/\n\s*\n/).map((para, i) => (
                  <p key={i} className={`leading-relaxed text-moss ${i === 0 ? "font-serif text-xl lg:text-2xl text-forest" : "text-base lg:text-lg"}`}>
                    {para}
                  </p>
                ))}
              </div>
            )}
          </motion.article>
        )}
      </main>
      <Footer />
    </div>
  );
}
